var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : new P(function (resolve) { resolve(result.value); }).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Assert, Preloader } from "@jetjs/utils";
/**
 * Worker Thread wraps the real web worker and handle the communication
 */
class WorkerThread {
    constructor(workerScript, free) {
        this.taskConfigs = 0;
        this.worker = new Worker(workerScript);
        this.worker.addEventListener("message", this.done.bind(this), false);
        this.free = free;
    }
    applyTaskConfiguration(taskConfigurations) {
        if (taskConfigurations.length === 0) {
            return this.done(null);
        }
        const job = {
            jobType: 0 /* APPLY_TASK_CONFIGURATION */,
            value: taskConfigurations
        };
        this.worker.postMessage(job);
        this.taskConfigs += taskConfigurations.length;
    }
    getTaskConfigurationCount() {
        return this.taskConfigs;
    }
    run(taskDef, resolve, reject) {
        Assert.notDefined(this.resolveCallback, "Worker Thread is currently busy and cannot run tasks.");
        Assert.notDefined(this.rejectCallback, "Worker Thread is currently busy and cannot run tasks.");
        this.resolveCallback = resolve;
        this.rejectCallback = reject;
        const job = {
            jobType: 1 /* PROCESS_TASK */,
            value: taskDef
        };
        this.worker.postMessage(job);
    }
    terminate() {
        this.worker.terminate();
    }
    done(event) {
        const response = event ? event.data : null;
        if (this.resolveCallback && this.rejectCallback && response) {
            switch (response.jobStatus) {
                case 0 /* SUCCESS */:
                    this.resolveCallback(response.value);
                    break;
                case 1 /* ERROR */:
                    this.rejectCallback(response.error);
                    break;
                default:
                    Assert.unreachable(response.jobStatus);
            }
        }
        // notify pool, that this thread can accept a new task
        this.resolveCallback = this.rejectCallback = undefined;
        this.free(this, response.jobType === 1 /* PROCESS_TASK */
            ? response.executionTime
            : undefined);
    }
}
/**
 * Scales the pool size
 */
class AutoScaler {
    constructor(min, max, free, threads) {
        /**
         * Queue that holds the MAX_PERF_MEASUREMENTS recent performance measurements
         */
        this.performanceMeasurements = [];
        /**
         * current average performance
         */
        this.currentAvgPerformance = 0;
        /**
         * Average performance in the last taken decision
         */
        this.previousAvgPerformance = 0;
        /**
         * Number of active workers in the pool
         */
        this.activeWorkers = 0;
        /**
         * Non active threads, taken from the pool to reduce the size of active worker.
         *
         * The Operating System (OS) performs load balancing to distribute the CPU usage among the running applications,
         * paying special attention when they collide in shared hardware resources, the logical cores.
         * According to our experiments, if the number of active workers actually decrements,
         * the OS reduces the CPU usage rate assigned to the web app, since the OS identifies lower CPU
         * demanding of this application. As a result, the web app performance is much lower.
         * However, with the use of fake decrements we prevent that the OS reduces CPU assignment to the web application,
         * as well as we also prevent to overload the system with too many threads.
         */
        this.fakeDecrements = [];
        this.takeNext = false;
        this.min = min;
        this.max = max;
        this.free = free;
        this.fakeDecrements = threads;
        // pool is always initialized with the min count of workers
        this.activeWorkers = min;
        this.intervalId = setInterval(this.check.bind(this), AutoScaler.CHECK_TIME_PERIOD_MS);
    }
    shutdown() {
        clearInterval(this.intervalId);
        this.fakeDecrements.forEach(w => w.terminate());
    }
    /**
     * Collects the performance measurements and initializes the variables if needed.
     */
    measurePerformance(perf) {
        this.performanceMeasurements.push(perf);
        if (this.previousAvgPerformance === 0 &&
            this.performanceMeasurements.length === AutoScaler.MAX_PERF_MEASUREMENTS) {
            // initialize previous average with the first values measured
            this.applyPrevious(this.averagePerformance());
        }
        else if (this.performanceMeasurements.length > AutoScaler.MAX_PERF_MEASUREMENTS) {
            // remove oldest measurement from queue, if enough data is available
            this.performanceMeasurements.shift();
        }
    }
    /**
     *
     * @param thread thread to take
     * @return true if the given thread should not stay in the pool. Otherwise false (not taken).
     */
    take(thread) {
        if (this.takeNext && this.activeWorkers > this.min) {
            this.fakeDecrements.push(thread);
            this.takeNext = false;
            this.activeWorkers -= 1;
            // take thread from pool
            return true;
        }
        // return false (not taken), so the pool knows, that this thread can still be used
        return false;
    }
    averagePerformance() {
        return (this.performanceMeasurements.reduce((prev, current) => prev + current, 0) / this.performanceMeasurements.length);
    }
    check() {
        if (this.performanceMeasurements.length !== AutoScaler.MAX_PERF_MEASUREMENTS) {
            // not enough data to run the algorithmus or the thread pool isn't busy
            return;
        }
        this.currentAvgPerformance = this.averagePerformance();
        if (this.currentAvgPerformance >
            this.previousAvgPerformance * AutoScaler.SPEEDUP_THRESHOLD) {
            if (this.takeNext) {
                this.takeNext = false;
            }
            else if (this.activeWorkers < this.max) {
                const worker = this.fakeDecrements.shift();
                if (worker) {
                    this.activeWorkers += 1;
                    this.free(worker);
                }
            }
            this.applyPrevious(this.currentAvgPerformance);
        }
        else if (this.currentAvgPerformance <
            this.previousAvgPerformance * AutoScaler.SLOWDOWN_THRESHOLD) {
            this.takeNext = true;
            this.applyPrevious(this.currentAvgPerformance);
        }
        // (Ausblick) TO-DO Try some optimizations: else: if threshold is not met, but pool is permanently busy scale up,
        // to see if there is any effect. After that the normal algo takes account of scaling
        // Because if we start with one Thread and this thread is always busy,
        // but has a constant processing time there will be no scaling because the Threshold is not met at any time
    }
    applyPrevious(avg) {
        // current perf measurement is now the previous one
        this.previousAvgPerformance = avg;
        // reset old performance measurements
        this.performanceMeasurements.splice(0, this.performanceMeasurements.length);
    }
}
/**
 * Time period in ms to check the if we have to made a decision
 */
AutoScaler.CHECK_TIME_PERIOD_MS = 1000;
/**
 * For both of them, larger values mean the variation has to be more significant. That is, a conservative scaling
 * setup, since the algorithm only takes a decision when there are important performance variations.
 * Whereas smaller values lead to eager scaling configuration,
 * because the algorithm takes decisions even though there are small performance variations.
 */
AutoScaler.SPEEDUP_THRESHOLD = 1.1;
AutoScaler.SLOWDOWN_THRESHOLD = 0.85;
AutoScaler.MAX_PERF_MEASUREMENTS = 10;
/**
 * Manages the Pool with WorkerThreads
 *
 * @public
 */
export class WorkerTaskExecutorPool {
    constructor(opts) {
        this.terminated = false;
        /**
         * stores all added configurations, needed to initiate new workers
         */
        this.taskConfigurations = [];
        /**
         * Array with queued tasks
         */
        // tslint:disable-next-line no-any Type can be different for each task
        this.taskQueue = [];
        /**
         * Worker queue with idle worker threads
         */
        this.workerQueue = [];
        this.options = Object.assign({}, opts, { 
            // Devs should use 2 if more multithreading is needed at startup. Because auto scaling needs at minimum 8 seconds to start take effect
            minWorker: opts.minWorker === undefined ? 1 : opts.minWorker, maxWorker: opts.maxWorker === undefined
                ? WorkerTaskExecutorPool.DEFAULT_MAX_WORKER_THREADS
                : opts.maxWorker });
        // validate input parameters (maximum of 16 Threads)
        Assert.inBetween(this.options.minWorker, 1, WorkerTaskExecutorPool.MAX_WORKER_THREADS);
        Assert.inBetween(this.options.maxWorker, this.options.minWorker, WorkerTaskExecutorPool.MAX_WORKER_THREADS);
        // initiate max worker
        this.times(this.options.maxWorker, this.initiateNewWorker.bind(this));
        // but only let min worker count active. Other Threads a are managed by the scaler
        this.scaler = new AutoScaler(this.options.minWorker, this.options.maxWorker, this.freeWorker.bind(this), this.workerQueue.splice(this.options.minWorker, this.options.maxWorker));
        this.preloader = new Preloader();
    }
    addTaskConfiguration(...config) {
        config.forEach(c => {
            if (c.preload) {
                this.preloader.preload(c.source, c.preloadPriority || "prefetch" /* LOW */);
            }
        });
        this.taskConfigurations.push(...config);
        // remove all idle workers from queue to apply configuration
        this.workerQueue.splice(0, this.workerQueue.length).forEach(wt => {
            if (!this.applyTaskConfigurationToWorkerThread(wt)) {
                // if no configuration will be applied, worker thread is idle
                this.workerQueue.push(wt);
            }
        });
    }
    submit(taskDef) {
        return __awaiter(this, void 0, void 0, function* () {
            // tslint:disable promise-must-complete
            return new Promise((resolve, reject) => {
                if (this.terminated) {
                    return reject(new Error("Thread-Pool is terminated."));
                }
                const thread = this.workerQueue.shift();
                if (thread !== undefined) {
                    thread.run(taskDef, resolve, reject);
                }
                else {
                    this.taskQueue.push({
                        resolve: resolve,
                        reject: reject,
                        definition: taskDef
                    });
                }
            });
            //tslint:enable
        });
    }
    shutdown() {
        this.terminated = true;
        return new Promise(resolve => {
            this.workerQueue.forEach(w => w.terminate());
            this.scaler.shutdown();
            // TO DO resolve promise after all workers are terminated?
            resolve();
        });
    }
    isTerminated() {
        return this.terminated;
    }
    freeWorker(worker, lastExecutionTime) {
        if (this.terminated) {
            return worker.terminate();
        }
        // use execution time to calculate scaling potentials (if available)
        // currently only used for jobs with Type equals to PROCESS_TASK
        if (lastExecutionTime) {
            this.scaler.measurePerformance(lastExecutionTime);
        }
        if (this.scaler.take(worker)) {
            // return, because worker should not stay in the pool anymore
            return;
        }
        // update to the newest task configuration
        if (this.applyTaskConfigurationToWorkerThread(worker)) {
            // worker thread is busy while applying new configuration
            return;
        }
        const task = this.taskQueue.shift();
        if (task !== undefined) {
            worker.run(task.definition, task.resolve, task.reject);
        }
        else {
            this.workerQueue.push(worker);
        }
    }
    /**
     * Applies the newest task configurations to the given worker
     * @param worker worker thread to update
     * @return boolean true if new configuration will be applied. false if no new configurations must be applied
     */
    applyTaskConfigurationToWorkerThread(worker) {
        const configDiff = this.taskConfigurations.slice(worker.getTaskConfigurationCount());
        if (configDiff.length > 0) {
            worker.applyTaskConfiguration(configDiff);
            return true;
        }
        return false;
    }
    initiateNewWorker() {
        const worker = new WorkerThread(this.options.workerScript, this.freeWorker.bind(this));
        if (!this.applyTaskConfigurationToWorkerThread(worker)) {
            this.workerQueue.push(worker);
        }
    }
    /**
     * calls the given function n times
     */
    times(n, fn) {
        for (let i = 0; i < n; i += 1) {
            fn();
        }
    }
}
WorkerTaskExecutorPool.MAX_WORKER_THREADS = 16;
/**
 * Besides, Chrome and Firefox, in Windows, show an almost linear performance improvement up to 3-4 workers,
 * but then it reaches a stable point. The main reason behind this behavior is that running
 * more threads increase collision on shared hardware resources.
 * That is, running more threads do not provide significant performance improvements.
 */
WorkerTaskExecutorPool.DEFAULT_MAX_WORKER_THREADS = 3;
//# sourceMappingURL=ExecutorPool.js.map